'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useReleaseNotes } from '@/hooks/useReleaseNotes';
import {
  formatReleaseNoteDate,
  RELEASE_NOTE_CONTENT_STYLES,
  sanitizeReleaseNoteHtml,
} from '@/lib/utils/release-notes';

function ReleaseNoteHtml({ sanitizedContent }: { sanitizedContent: string }) {
  // biome-ignore lint/security/noDangerouslySetInnerHtml: content is sanitized by sanitizeReleaseNoteHtml before rendering.
  return <div className={RELEASE_NOTE_CONTENT_STYLES} dangerouslySetInnerHTML={{ __html: sanitizedContent }} />;
}

const RELEASE_OVERLAY_PROPS = { 'data-testid': 'release-overlay' } as const;

export function ReleaseNotesDialog() {
  const { releaseNotes, shouldShowDialog, dismissDialog, isSuccess } = useReleaseNotes();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isSuccess && shouldShowDialog) {
      setOpen(true);
    }
  }, [isSuccess, shouldShowDialog]);

  const handleClose = useCallback(() => {
    dismissDialog();
    setOpen(false);
  }, [dismissDialog]);

  const latestNote = releaseNotes.length > 0 ? releaseNotes[0] : null;

  const sanitizedContent = useMemo(() => {
    if (!latestNote) return '';
    return sanitizeReleaseNoteHtml(latestNote.content);
  }, [latestNote]);

  if (!latestNote) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent
        data-testid="release-notes-dialog"
        className="max-h-[80vh] overflow-y-auto sm:max-w-[800px]"
        overlayProps={RELEASE_OVERLAY_PROPS}
      >
        <DialogHeader>
          <DialogTitle>{latestNote.title}</DialogTitle>
          <DialogDescription>
            {formatReleaseNoteDate(latestNote.publishedAt)}
          </DialogDescription>
        </DialogHeader>
        <ReleaseNoteHtml sanitizedContent={sanitizedContent} />
        <DialogFooter>
          <Button onClick={handleClose}>閉じる</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
